import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { CanisterService } from '../../../../services/canister.service';
import { Canister } from './canister.data';
import { EventService } from '@/surveys/services/event.service';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { Component, Input, OnDestroy, OnInit, inject, signal, computed, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SessionStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-canister',
    templateUrl: './canister.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule
    ]
})
export class CanisterComponent implements OnInit, OnDestroy {
    // Services using inject()
    private destroyRef = inject(DestroyRef);
    public router = inject(Router);
    public route = inject(ActivatedRoute);
    private store = inject(Store<PortalUserState>);
    public canisterService = inject(CanisterService);
    public paradataEventService = inject(ParadataEventService);
    public eventService = inject(EventService);
    public sessionStorageService = inject(SessionStorageService);

    // Signals for reactive state
    user = signal<PortalUser | null>(null);
    canisterType = signal<string>('');
    canisterImageData = signal<string>('');
    canisterAltText = signal<string>('');
    canisters = signal<Canister[]>([]);

    // Computed signals
    currentUser = computed(() => this.user());
    currentCanisterType = computed(() => this.canisterType());
    currentCanisterImageData = computed(() => this.canisterImageData());
    currentCanisterAltText = computed(() => this.canisterAltText());
    currentCanisters = computed(() => this.canisters());

    // Legacy subscriptions for compatibility
    channel$: Subscription;
    event$: Subscription;
    user$: Subscription;

    constructor() {
        this.user$ = this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                user => {
                    this.user.set(user);
                }
            );
        this.event$ = this.eventService.eventListner()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                event => {
                    if ( event.canisterTriggered ) {
                        let canisterData = '';
                        if ( event.canisterTriggered.surveys ) {
                              // Format list of entries for consumption by Pega
                            let surveyIDs = this.extractViableSurveys( event.canisterTriggered.surveys, 'surveyId' );
                            let canisterCodes = this.extractViableSurveys( event.canisterTriggered.surveys, 'canisterCode' );
                            let canisterTriggers = this.extractViableSurveys( event.canisterTriggered.surveys, 'canisterTrigger' );
                            canisterData = this.buildCanisterData( surveyIDs, canisterCodes, canisterTriggers );
                        } else {
                            canisterData = event.canisterTriggered.surveyId.toString() + ',' + event.canisterTriggered.canisterCode.toString();
                        }

                        this.getCanistersBySurvey( canisterData );
                    }
                }
            );
    }

      // Pull property from survey list entries, fitler valid results, then concat to comma-separated string
    extractViableSurveys( surveys: SurveyCard[], property: string ) {
        return surveys.map( survey => {
              // Check to make sure canisterCode is populated so there's no chance of the two arrays being skewed from one another
            if ( survey.canisterTrigger && survey.canisterTrigger.includes('PageLoad') && survey.canisterCode ) {
                return survey[ property ];
            }
        }).filter( prop => {
            return prop;
        });
    }
 
      // Interleave surveyID and canisterCode
    buildCanisterData( surveyIDs: string[], canisterCodes: string[], canisterTriggers: string[] ) {
        let canisterData = [];
        for ( let i = 0; i < surveyIDs.length; i++ ) {
            let currentCanisterCodes = canisterCodes[i].split(",");
            let currentCanisterTriggers = canisterTriggers[i].split(",");
            currentCanisterCodes.forEach((canCode, index) => {
                if (currentCanisterTriggers[index] === 'PageLoad') {
                    canisterData.push( surveyIDs[i] + ',' + canCode );
                }
            });
        }

          // Remove any duplicate combinations
        let uniqueData = [];
        for ( let i = 0; i < canisterData.length; i++ ) {
            if ( uniqueData.indexOf( canisterData[i] ) === -1 ) {
                uniqueData.push( canisterData[i] );
            }
        }

        return uniqueData.join( '|' );
    }

      // Retrieve and map canister data
    getCanistersBySurvey( canisterData: string ) {
        if ( canisterData ) {
            this.channel$ = this.canisterService.getCanistersBySurvey( canisterData )
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(
                    response => {
                        if ( response && response.pxResults ) {
                            const results = response.pxResults;
                            this.mapCanisterData( results, canisterData );
                        }
                    }
                );
        }
    }

      // Map raw canister data from Pega so it's easier to work with
    mapCanisterData( rawCanisters, canisterData: string ) {
        let canisterSurveys = canisterData.split("|");
        canisterSurveys.forEach((survey, index) => {
            canisterSurveys[index] = survey.split(",")[0];
        });

        let canisters = [];
        rawCanisters.forEach( (rawCanister, index) => {
              // Avoid duplicating canisters
            if ( this.canisters().map( canister => { return canister.articleID; } ).indexOf( rawCanister.ArticleID ) === -1 ) {
                let canister = {
                    articleID: rawCanister.ArticleID,
                    type: rawCanister.CanisterType,
                    attachments: [],
                    surveyID: canisterSurveys[index]
                };

                  // Map each attachment
                rawCanister.CanisterAttachments?.forEach( rawAttachment => {
                    let attachment = {
                        link: rawAttachment.AttachmentLink,
                        ID: rawAttachment.pxLinkedRefTo,
                        type: rawAttachment.pyCategory,
                        imageData: ''
                    };

                      // Image data wil be populated when the call completes
                    this.canisterService.getResourceBase64( attachment.ID ).subscribe(
                        response => {
                            attachment.imageData = response;
                        }
                    );

                    canister.attachments.push( attachment );
                });

                canisters.push( canister );
            }
        });

          // Canisters come in from various trigger points and calls so they aren't replaced by each one but added to the list
        this.canisters.set(this.canisters().concat( canisters ));
    }

      // Get raw base64 data of attachment image from Pega
    getAttachmentData( attachmentKey: string ) {
        this.canisterService.getResourceBase64( attachmentKey ).subscribe(
            response => {
                return response;
            }
        );
    }

    canisterClick(surveyId: number) {
        this.paradataEventService.add(ParadataEventEnum.Canister, surveyId);
    }

    ngOnInit(): void {
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        // Subscriptions are now handled by takeUntilDestroyed, no manual unsubscribe needed here.
    }

}
